import { supabase } from '../supabase';
import {
  countClientsByPartner,
  pickProfilesByPartner,
  tagIdsByPartner,
  type ReferrerProfileLite,
} from '../partners/partnerReferrerProfile';

export interface PartnerReferrerOverview {
  /** profilo provvigioni per partner (il piu' vecchio se ce ne sono due) */
  profiles: Map<string, ReferrerProfileLite>;
  /** nome del gruppo segnalatori (se il profilo ne ha uno, la provvigione viene dal gruppo) */
  groupNames: Map<string, string>;
  /** clienti/lead portati (clients.referrer_id) per partner */
  clientCounts: Map<string, number>;
  /** tag del profilo segnalatore per partner (unione dei profili) */
  tagIds: Map<string, string[]>;
}

const CHUNK = 100;

function chunks<T>(items: T[]): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += CHUNK) out.push(items.slice(i, i + CHUNK));
  return out;
}

/**
 * Per la tabella Partner: profili provvigioni collegati, nomi dei gruppi e
 * numero di clienti/lead portati. Solo letture; gli errori non bloccano la
 * tabella (colonne mostrate vuote).
 */
export async function fetchPartnerReferrerOverview(partnerIds: string[]): Promise<PartnerReferrerOverview> {
  const empty: PartnerReferrerOverview = { profiles: new Map(), groupNames: new Map(), clientCounts: new Map(), tagIds: new Map() };
  if (partnerIds.length === 0) return empty;

  try {
    const profileRows: ReferrerProfileLite[] = [];
    for (const ids of chunks(partnerIds)) {
      const { data, error } = await supabase
        .from('referrers')
        .select('id, external_partner_id, commission_rate, commission_type, commission_fixed_amount, referrer_group_id, created_at, tag_ids')
        .in('external_partner_id', ids);
      if (error) throw error;
      profileRows.push(...((data || []) as ReferrerProfileLite[]));
    }

    const profiles = pickProfilesByPartner(profileRows);

    // Tutti i profili (anche gli eventuali doppioni) contano per i clienti portati
    const partnerByReferrerId = new Map<string, string>();
    profileRows.forEach((r) => { if (r.external_partner_id) partnerByReferrerId.set(r.id, r.external_partner_id); });

    const groupIds = Array.from(new Set(Array.from(profiles.values()).map((p) => p.referrer_group_id).filter(Boolean) as string[]));
    const groupNames = new Map<string, string>();
    if (groupIds.length > 0) {
      const { data } = await supabase.from('referrer_groups').select('id, group_name').in('id', groupIds);
      (data || []).forEach((g: { id: string; group_name: string }) => groupNames.set(g.id, g.group_name));
    }

    const referrerIds: (string | null)[] = [];
    for (const ids of chunks(Array.from(partnerByReferrerId.keys()))) {
      const { data, error } = await supabase.from('clients').select('referrer_id').in('referrer_id', ids);
      if (error) break;
      (data || []).forEach((c: { referrer_id: string | null }) => referrerIds.push(c.referrer_id));
    }

    return {
      profiles,
      groupNames,
      clientCounts: countClientsByPartner(referrerIds, partnerByReferrerId),
      tagIds: tagIdsByPartner(profileRows),
    };
  } catch (error) {
    console.error('[partnerReferrerProfiles] Errore nel caricamento dei profili provvigioni:', error);
    return empty;
  }
}

// Avviso "profilo provvigioni cambiato" (es. provvigione modificata dalla scheda):
// la tabella Partner ricarica la colonna Provvigione senza rileggere tutti i partner.
const listeners = new Set<() => void>();

export function onPartnerProfilesChanged(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

export function notifyPartnerProfilesChanged(): void {
  listeners.forEach((listener) => listener());
}
